import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import { post } from "../../Services/ApiRequest";
import { API_URLS } from "../../Utils/Constants/APIConstants";

interface BriefDocumentState {
  watchListBriefing: any | null;
  watchListBriefingLoading: boolean;
  watchListBriefingError: string | null;
  generateBriefingLoading: boolean;
  generateBriefingError: string | null;
  activeGenerateBriefingRequests: number;
}

const initialState: BriefDocumentState = {
  watchListBriefing: null,
  watchListBriefingLoading: false,
  watchListBriefingError: null,
  generateBriefingLoading: false,
  generateBriefingError: null,
  activeGenerateBriefingRequests: 0,
};

const ABORTED_BRIEFING = "ABORTED_BRIEFING";

export const generateWatchListBriefing = createAsyncThunk(
  "briefDocument/generateWatchListBriefing",
  async (
    payload: {
      account_ids?: string[];
      account_scope?: "all";
      user_id?: string | null;
      user_ids?: string[];
      signal?: AbortSignal;
    },
    { rejectWithValue }
  ) => {
    try {
      const { signal, ...body } = payload;
      const response = await post(
        API_URLS.generateWatchListBriefing(),
        body,
        undefined,
        signal ? { signal } : undefined
      );
      return response;
    } catch (error: any) {
      if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED") {
        return rejectWithValue(ABORTED_BRIEFING);
      }
      return rejectWithValue(
        error.message || "Failed to generate watchlist briefing"
      );
    }
  }
);

const BriefDocumentSlice = createSlice({
  name: "briefDocument",
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(generateWatchListBriefing.pending, state => {
        state.activeGenerateBriefingRequests += 1;
        state.generateBriefingLoading = true;
        state.watchListBriefingLoading = true;
        state.generateBriefingError = null;
        state.watchListBriefingError = null;
      })
      .addCase(generateWatchListBriefing.fulfilled, (state, action) => {
        state.activeGenerateBriefingRequests = Math.max(
          0,
          state.activeGenerateBriefingRequests - 1
        );
        const hasPendingRequests = state.activeGenerateBriefingRequests > 0;
        state.generateBriefingLoading = hasPendingRequests;
        state.watchListBriefingLoading = hasPendingRequests;
        state.watchListBriefing = action.payload;
        state.generateBriefingError = null;
        state.watchListBriefingError = null;
      })
      .addCase(generateWatchListBriefing.rejected, (state, action) => {
        state.activeGenerateBriefingRequests = Math.max(
          0,
          state.activeGenerateBriefingRequests - 1
        );
        const hasPendingRequests = state.activeGenerateBriefingRequests > 0;
        state.generateBriefingLoading = hasPendingRequests;
        state.watchListBriefingLoading = hasPendingRequests;
        if (action.payload === ABORTED_BRIEFING) return;
        state.generateBriefingError = action.payload as string;
        state.watchListBriefingError = action.payload as string;
        state.watchListBriefing = null;
      });
  },
});

export default BriefDocumentSlice.reducer;
